import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Loading from '../../components/Loading';
import { CheckCircle2, ShieldAlert, Sparkles, CreditCard, Calendar, Download, Receipt } from 'lucide-react';

export default function SubscriptionSettings() {
    const { user } = useAuth();
    const toast = useToast();
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [subDetails, setSubDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('plans'); // 'plans' or 'billing'
    const [loadingDetails, setLoadingDetails] = useState(true);

    const currentPlan = subDetails?.plan_name || user?.plan_name || 'Starter';
    const isTrialing = subDetails?.subscription_status === 'trialing' || user?.subscription_status === 'trialing';
    const trialEnd = subDetails?.trial_end || user?.trial_end;

    const fetchSubDetails = async () => {
        try {
            const response = await api.get('/api/restaurants/subscription/details');
            setSubDetails(response.data);
        } catch (err) {
            console.error('Failed to load subscription details', err);
        } finally {
            setLoadingDetails(false);
        }
    };

    useEffect(() => {
        fetchSubDetails();
    }, []);

    const plans = [
        {
            name: 'Starter',
            price: '₹2,999',
            description: 'Essential features for simple operations',
            features: [
                'Restaurant Website',
                'Customer Ordering System',
                'Admin Dashboard',
                'Online Payments Support',
                '500 orders/month',
                'Technical Support'
            ],
            limits: [
                'No Kitchen Display System (KDS)',
                'No Delivery Partner Dashboards'
            ]
        },
        {
            name: 'Professional',
            price: '₹6,999',
            description: 'Advanced tools for busy cloud kitchens',
            features: [
                'Restaurant Website',
                'Customer Ordering System',
                'Admin Dashboard',
                'Online Payments Support',
                'Kitchen Display System (KDS)',
                'Customer Mobile App',
                'Admin Mobile App',
                'Live Chatbot Integration',
                'Unlimited orders',
                'Technical Support'
            ],
            limits: [
                'No Delivery Partner Dashboards'
            ]
        },
        {
            name: 'Premium',
            price: '₹9,999',
            description: 'Complete multi-terminal restaurant suite',
            features: [
                'Restaurant Website',
                'Customer Ordering System',
                'Admin Dashboard',
                'Online Payments Support',
                'Kitchen Display System (KDS)',
                'Delivery Partner Dashboards',
                'Customer Mobile App',
                'Admin Mobile App',
                'Kitchen & Delivery Mobile Apps',
                'Live Chatbot Integration',
                'WhatsApp Order Notifications',
                'Unlimited orders',
                'Priority Support'
            ],
            limits: []
        }
    ];

    const handleUpgrade = async (planName) => {
        setLoadingPlan(planName);
        try {
            const response = await api.post('/api/restaurants/subscription/upgrade', { planName });
            
            if (response.data.paymentUrl) {
                // Razorpay requires card re-authorization for plan changes
                toast.success(`Plan changed to ${planName}! Redirecting for card authorization...`);
                window.open(response.data.paymentUrl, '_blank');
            } else {
                toast.success(`Successfully switched to ${planName} plan!`);
            }
            
            fetchSubDetails();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update plan');
        } finally {
            setLoadingPlan(null);
        }
    };

    const handleApproveUpgrade = async () => {
        setLoadingPlan(subDetails?.pending_upgrade_plan);
        try {
            const response = await api.post('/api/restaurants/subscription/approve-upgrade');
            
            if (response.data.subscriptionId && response.data.keyId) {
                // Dynamically load Razorpay Checkout script
                const rzpScript = document.createElement('script');
                rzpScript.src = 'https://checkout.razorpay.com/v1/checkout.js';
                rzpScript.async = true;
                document.body.appendChild(rzpScript);

                rzpScript.onload = () => {
                    const options = {
                        key: response.data.keyId,
                        subscription_id: response.data.subscriptionId,
                        name: 'Aaharam Cloud',
                        description: `Upgrade Plan - ${subDetails?.pending_upgrade_plan || 'SaaS'} Plan`,
                        config: {
                            display: {
                                blocks: {
                                    cardBlock: {
                                        name: 'Pay using Card',
                                        instruments: [{ method: 'card' }]
                                    }
                                },
                                sequence: ['block.cardBlock'],
                                preferences: {
                                    show_default_blocks: false
                                }
                            }
                        },
                        handler: function (rzpRes) {
                            toast.success('Card mandate authorized successfully! Upgraded plan is active.');
                            fetchSubDetails();
                        },
                        modal: {
                            ondismiss: function () {
                                fetchSubDetails();
                            }
                        }
                    };
                    const rzp = new window.Razorpay(options);
                    rzp.open();
                };
            } else {
                toast.success('Upgrade approved successfully!');
                fetchSubDetails();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to approve upgrade');
        } finally {
            setLoadingPlan(null);
        }
    };

    const handleDownloadInvoice = (invoice) => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Invoice ${invoice.id}</title>
                <style>
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
                    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #f3f4f6; padding-bottom: 20px; }
                    .company-logo { font-size: 24px; font-weight: bold; color: #FDB515; }
                    .invoice-title { font-size: 28px; font-weight: 900; text-align: right; }
                    .details { margin: 40px 0; display: flex; justify-content: space-between; }
                    .footer { margin-top: 50px; text-align: center; color: #9ca3af; font-size: 12px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div>
                        <div class="company-logo">Aaharam Cloud</div>
                        <p style="margin: 5px 0; font-size: 14px;">Next-Gen Cloud Kitchen SaaS</p>
                    </div>
                    <div>
                        <div class="invoice-title">INVOICE</div>
                        <p style="margin: 5px 0; font-size: 14px; text-align: right;">${invoice.id}</p>
                    </div>
                </div>
                <div class="details">
                    <div>
                        <strong>Billed To:</strong>
                        <p style="margin: 5px 0; font-size: 14px;">${subDetails?.name || 'Restaurant'}</p>
                        <p style="margin: 5px 0; font-size: 14px;">Slug: ${subDetails?.slug || ''}</p>
                    </div>
                    <div style="text-align: right;">
                        <p style="margin: 5px 0; font-size: 14px;"><strong>Invoice Date:</strong> ${invoice.date}</p>
                        <p style="margin: 5px 0; font-size: 14px;"><strong>Payment Method:</strong> ${invoice.method}</p>
                        <p style="margin: 5px 0; font-size: 14px;"><strong>Status:</strong> <span style="color: #10b981; font-weight: bold;">${invoice.status}</span></p>
                    </div>
                </div>
                <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
                    <thead>
                        <tr>
                            <th style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: left; background-color: #f9fafb;">Description</th>
                            <th style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: right; background-color: #f9fafb;">Qty</th>
                            <th style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: right; background-color: #f9fafb;">Rate</th>
                            <th style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: right; background-color: #f9fafb;">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: left;">Aaharam Cloud Platform Subscription - ${invoice.plan} Plan</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: right;">1</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: right;">${invoice.amount}</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f3f4f6; text-align: right;">${invoice.amount}</td>
                        </tr>
                    </tbody>
                </table>
                <div style="text-align: right; font-size: 20px; font-weight: bold; margin-top: 30px;">
                    Total: ${invoice.amount}
                </div>
                <div class="footer">
                    Thank you for partnering with Aaharam Cloud!<br/>
                    This is a computer-generated invoice receipt.
                </div>
                <script>
                    window.onload = function() {
                        document.title = "invoice_${invoice.id}";
                        window.print();
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.title = `invoice_${invoice.id}`;
        printWindow.document.close();
    };

    if (loadingDetails) {
        return <Loading />;
    }

    return (
        <div className="space-y-8 max-w-5xl mx-auto text-left">
            {/* Header & Tabs */}
            <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-gray-200 pb-2 gap-4">
                <div>
                    <h1 className="text-3xl font-black text-gray-900">Subscription & Billing</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage your platform plan, view invoices, and update billing parameters.</p>
                </div>
                <div className="flex bg-gray-100 p-1.5 rounded-2xl self-start">
                    <button
                        onClick={() => setActiveTab('plans')}
                        className={`py-2 px-5 text-xs font-bold rounded-xl transition-all ${
                            activeTab === 'plans'
                                ? 'bg-white text-primary-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Plans & Upgrades
                    </button>
                    <button
                        onClick={() => setActiveTab('billing')}
                        className={`py-2 px-5 text-xs font-bold rounded-xl transition-all ${
                            activeTab === 'billing'
                                ? 'bg-white text-primary-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Billing & Invoices
                    </button>
                </div>
            </div>

            {/* Proposed Upgrade Banner */}
            {subDetails?.pending_upgrade_status === 'pending_customer_approval' && subDetails?.pending_upgrade_plan && (
                <div className="bg-gradient-to-r from-primary-600 to-indigo-600 p-6 rounded-3xl text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border border-primary-500/20">
                    <div className="space-y-1">
                        <span className="bg-white/20 text-white font-black text-[9px] uppercase px-3 py-0.5 rounded-full tracking-widest inline-block">
                            Action Required
                        </span>
                        <h3 className="text-lg font-black tracking-tight flex items-center">
                            <Sparkles className="w-5 h-5 mr-2 text-amber-300 animate-pulse" />
                            Plan Upgrade Proposal: {subDetails.pending_upgrade_plan}
                        </h3>
                        <p className="text-xs text-primary-100 font-medium">
                            The platform administrator has proposed to upgrade your subscription plan to <span className="font-extrabold text-white">{subDetails.pending_upgrade_plan}</span>. 
                            To accept and configure billing, please authorize the mandate.
                        </p>
                    </div>
                    <button
                        onClick={handleApproveUpgrade}
                        disabled={loadingPlan !== null}
                        className="bg-white text-primary-700 hover:bg-slate-50 font-black text-xs px-6 py-3 rounded-xl shadow-md uppercase tracking-wider transition-all active:scale-95 flex-shrink-0"
                    >
                        {loadingPlan ? 'Processing...' : 'Approve & Authorize'}
                    </button>
                </div>
            )}

            {/* Current Plan Banner */}
            <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="text-left">
                    <h2 className="text-lg font-bold text-gray-900">Current Subscription</h2>
                    <div className="flex items-center space-x-2 mt-2">
                        <span className="bg-primary-100 text-primary-700 text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider">
                            Plan: {currentPlan}
                        </span>
                        {isTrialing ? (
                            <span className="bg-amber-100 text-amber-700 text-xs px-3 py-1 rounded-full font-bold">
                                Free Trialing
                            </span>
                        ) : (
                            <span className="bg-emerald-100 text-emerald-700 text-xs px-3 py-1 rounded-full font-bold">
                                Active Subscription
                            </span>
                        )}
                    </div>
                    {isTrialing && trialEnd && (
                        <p className="text-xs text-gray-500 mt-2">
                            Your trial period ends on <span className="font-bold">{new Date(trialEnd).toLocaleDateString()}</span>. All features are currently unlocked.
                        </p>
                    )}
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-gray-100 text-right min-w-[200px]">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Pricing Model</p>
                    <p className="text-2xl font-black text-slate-800">
                        {currentPlan === 'Starter' ? '₹2,999' : currentPlan === 'Professional' ? '₹6,999' : '₹9,999'}
                        <span className="text-xs text-gray-400 font-normal"> /mo</span>
                    </p>
                </div>
            </div>

            {activeTab === 'plans' ? (
                /* Plans Tab */
                <div className="grid md:grid-cols-3 gap-8">
                    {plans.map((p) => {
                        const isCurrent = currentPlan === p.name;
                        return (
                            <div 
                                key={p.name} 
                                className={`bg-white rounded-3xl p-8 border-2 flex flex-col justify-between shadow-sm relative text-left ${
                                    isCurrent ? 'border-primary-500 shadow-md ring-2 ring-primary-500/20' : 'border-gray-200'
                                }`}
                            >
                                {isCurrent && (
                                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary-500 text-white font-black text-[10px] uppercase px-4 py-1 rounded-full tracking-widest flex items-center">
                                        <Sparkles className="w-3 h-3 mr-1" /> Active Plan
                                    </span>
                                )}
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">{p.name}</h3>
                                    <p className="text-xs text-gray-400 mt-1">{p.description}</p>
                                    <div className="my-6 flex items-baseline">
                                        <span className="text-3xl font-black text-gray-900">{p.price}</span>
                                        <span className="text-gray-400 font-bold text-xs ml-1">/ month</span>
                                    </div>
                                    <div className="space-y-4 mb-8 pt-4 border-t border-gray-100">
                                        <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Included Features</p>
                                        <ul className="space-y-2">
                                            {p.features.map((f, i) => (
                                                <li key={i} className="flex items-start text-xs text-gray-600 font-medium">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        {p.limits.length > 0 && (
                                            <>
                                                <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest pt-2">Exclusions</p>
                                                <ul className="space-y-2">
                                                    {p.limits.map((l, i) => (
                                                        <li key={i} className="flex items-start text-xs text-gray-400 font-medium">
                                                            <ShieldAlert className="w-4 h-4 text-rose-400 mr-2 mt-0.5 flex-shrink-0" />
                                                            <span>{l}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        const actionType = currentPlan === p.name ? 'Active' : 'Request';
                                        if (actionType === 'Request') {
                                            const planOrder = { 'Starter': 1, 'Professional': 2, 'Premium': 3 };
                                            const isUpgrade = planOrder[p.name] > planOrder[currentPlan];
                                            const subject = encodeURIComponent(`${isUpgrade ? 'Upgrade' : 'Downgrade'} Request - ${subDetails?.name || user?.full_name}`);
                                            const body = encodeURIComponent(
                                                `Hi Aaharam Cloud Support,\n\nWe would like to request a plan change for our restaurant account:\n` +
                                                `- Restaurant Name: ${subDetails?.name || ''}\n` +
                                                `- Slug: ${subDetails?.slug || ''}\n` +
                                                `- Current Plan: ${currentPlan}\n` +
                                                `- Requested Plan: ${p.name}\n\nPlease process this ${isUpgrade ? 'upgrade' : 'downgrade'}. Thank you!`
                                            );
                                            window.location.href = `mailto:support@aaharamcloud.in?subject=${subject}&body=${body}`;
                                        }
                                    }}
                                    disabled={isCurrent}
                                    className={`w-full py-3.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center ${
                                        isCurrent
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                            : 'bg-primary-600 hover:bg-primary-700 text-white shadow-md active:scale-95'
                                    }`}
                                >
                                    {isCurrent 
                                        ? 'Current Plan' 
                                        : (() => {
                                            const planOrder = { 'Starter': 1, 'Professional': 2, 'Premium': 3 };
                                            if (planOrder[p.name] < planOrder[currentPlan]) {
                                                return 'Request Downgrade';
                                            }
                                            return 'Request Upgrade';
                                          })()
                                    }
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* Billing & Invoices Tab */
                <div className="grid md:grid-cols-3 gap-8">
                    {/* Billing Details & Cards */}
                    <div className="md:col-span-1 space-y-6">
                        {/* Saved Card */}
                        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
                            <h3 className="text-sm font-bold text-gray-900 flex items-center">
                                <CreditCard className="w-4 h-4 text-primary-500 mr-2" />
                                Payment Method
                            </h3>
                            {subDetails?.card_last4 ? (
                                <div className="p-4 bg-slate-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="bg-primary-100 text-primary-700 p-2.5 rounded-xl text-xs font-black uppercase tracking-wider">
                                            {subDetails?.card_brand || 'Card'}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-800">•••• •••• •••• {subDetails?.card_last4}</p>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Authorized Card Mandate</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-2xl">
                                    <p className="text-xs text-gray-400 font-medium">No saved credit cards found</p>
                                    <p className="text-[10px] text-gray-300 font-bold uppercase tracking-wide mt-1">Free Trial Mode</p>
                                </div>
                            )}
                        </div>

                        {/* Billing Schedule */}
                        <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
                            <h3 className="text-sm font-bold text-gray-900 flex items-center">
                                <Calendar className="w-4 h-4 text-primary-500 mr-2" />
                                Renewal Details
                            </h3>
                            <div className="space-y-3 text-xs">
                                <div className="flex justify-between py-1 border-b border-gray-50">
                                    <span className="text-gray-400 font-medium">Billing Cycle</span>
                                    <span className="text-gray-800 font-bold uppercase">{subDetails?.billing_cycle || 'Monthly'}</span>
                                </div>
                                <div className="flex justify-between py-1 border-b border-gray-50">
                                    <span className="text-gray-400 font-medium">Subscription Status</span>
                                    <span className="text-emerald-600 font-bold uppercase">{subDetails?.subscription_status || 'Trialing'}</span>
                                </div>
                                <div className="flex justify-between py-1 border-b border-gray-50">
                                    <span className="text-gray-400 font-medium">Next Invoice</span>
                                    <span className="text-gray-800 font-bold">
                                        {subDetails?.next_invoice_date 
                                            ? new Date(subDetails.next_invoice_date).toLocaleDateString()
                                            : (subDetails?.trial_end 
                                                ? new Date(subDetails.trial_end).toLocaleDateString()
                                                : new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleDateString())
                                        }
                                    </span>
                                </div>
                                {subDetails?.customer_id && (
                                    <div className="flex justify-between py-1 border-b border-gray-50">
                                        <span className="text-gray-400 font-medium">Customer ID</span>
                                        <span className="text-gray-700 font-mono font-bold select-all">{subDetails.customer_id}</span>
                                    </div>
                                )}
                                {subDetails?.aggregator_subscription_id && (
                                    <div className="flex justify-between py-1">
                                        <span className="text-gray-400 font-medium">Subscription ID</span>
                                        <span className="text-gray-700 font-mono font-bold select-all">{subDetails.aggregator_subscription_id}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Invoice History */}
                    <div className="md:col-span-2 bg-white rounded-3xl border border-gray-200 shadow-sm p-6 space-y-4">
                        <h3 className="text-sm font-bold text-gray-900 flex items-center">
                            <Receipt className="w-4 h-4 text-primary-500 mr-2" />
                            Invoice History
                        </h3>
                        {subDetails?.invoices && subDetails.invoices.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs text-left">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-gray-400 font-bold uppercase tracking-wider">
                                            <th className="pb-3">Invoice ID</th>
                                            <th className="pb-3">Billing Date</th>
                                            <th className="pb-3">Plan</th>
                                            <th className="pb-3">Amount</th>
                                            <th className="pb-3">Status</th>
                                            <th className="pb-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {subDetails.invoices.map((inv) => (
                                            <tr key={inv.id} className="text-gray-700">
                                                <td className="py-4 font-mono font-bold text-gray-900 truncate max-w-[120px]" title={inv.id}>{inv.id}</td>
                                                <td className="py-4">{new Date(inv.date).toLocaleDateString()}</td>
                                                <td className="py-4 font-bold">{inv.plan}</td>
                                                <td className="py-4 font-bold">{inv.amount}</td>
                                                <td className="py-4">
                                                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold uppercase text-[9px] tracking-wider">
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td className="py-4 text-right">
                                                    {inv.pdfUrl ? (
                                                        <a 
                                                            href={inv.pdfUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center space-x-1 text-primary-600 hover:text-primary-700 font-bold"
                                                        >
                                                            <Download className="w-3.5 h-3.5" />
                                                            <span>Download PDF</span>
                                                        </a>
                                                    ) : (
                                                        <button 
                                                            onClick={() => handleDownloadInvoice(inv)}
                                                            className="inline-flex items-center space-x-1 text-primary-600 hover:text-primary-700 font-bold"
                                                        >
                                                            <Download className="w-3.5 h-3.5" />
                                                            <span>Print Receipt</span>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-400 font-medium">
                                No invoice records found for this account.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
